import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { unwrapList, apiErrorMessage } from '@/lib/api-helpers';
import { Button } from '@ouropix/ui';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import { Label } from '@ouropix/ui';
import { Textarea } from '@ouropix/ui';
import { Switch } from '@ouropix/ui';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ouropix/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ouropix/ui';
import { Plus, Pencil, Trash2, Layers, Settings2, Code } from 'lucide-react';
import AdminPageHeader from '@/components/layout/PageHeader';
import { toast } from 'sonner';
import type { GameTemplate } from '@/components/games/types';

export const Route = createFileRoute('/_admin/jogos/templates')({
  component: TemplatesPage,
});

const COMPONENT_OPTIONS = [
  { value: 'SlotMachine', label: 'Slot Machine' },
  { value: 'ScratchCard', label: 'Raspadinha' },
  { value: 'MysteryBox', label: 'Caixa Misteriosa' },
  { value: 'WheelOfFortune', label: 'Roleta da Sorte' },
  { value: 'GenericGame', label: 'Jogo Generico' },
];

const DEFAULT_FORM = {
  modality_id: '',
  name: '',
  code: '',
  description: '',
  component_name: 'SlotMachine',
  config: '{}',
  preview_image_url: '',
  active: true,
};

function generateCodeFromName(name: string) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function TemplatesPage() {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<GameTemplate | null>(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  const { data: templates, isLoading } = useQuery<GameTemplate[]>({
    queryKey: ['game-templates'],
    queryFn: () => api.get('/admin/templates').then(r => unwrapList(r.data, 'templates')),
    select: (data: GameTemplate[]) =>
      data.map(t => ({
        ...t,
        config: typeof t.config === 'string'
          ? (() => { try { return JSON.parse(t.config as unknown as string); } catch { return {}; } })()
          : t.config,
      })),
    refetchInterval: 15000,
  });

  const { data: modalities } = useQuery<{ id: string; name: string; code: string; icon: string | null }[]>({
    queryKey: ['game-modalities'],
    queryFn: () => api.get('/admin/modalities').then(r => unwrapList(r.data, 'modalities')),
  });

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData & { id?: string }) => {
      let config: Record<string, unknown> = {};
      try {
        config = JSON.parse(data.config);
      } catch {
        throw new Error('Configuração JSON inválida');
      }

      const payload = {
        modality_id: data.modality_id,
        name: data.name,
        code: data.code,
        description: data.description || null,
        component_name: data.component_name,
        config,
        preview_image_url: data.preview_image_url || null,
        active: data.active,
      };

      if (data.id) {
        await api.put(`/admin/templates/${data.id}`, payload);
      } else {
        await api.post('/admin/templates', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['game-templates'] });
      setIsDialogOpen(false);
      resetForm();
      toast.success(editingTemplate ? 'Template atualizado!' : 'Template criado!');
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Erro ao salvar template'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['game-templates'] });
      toast.success('Template removido!');
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Erro ao remover template'));
    },
  });

  const resetForm = () => {
    setFormData(DEFAULT_FORM);
    setEditingTemplate(null);
  };

  const handleEdit = (template: GameTemplate) => {
    setEditingTemplate(template);
    setFormData({
      modality_id: template.modality_id,
      name: template.name,
      code: template.code,
      description: template.description || '',
      component_name: template.component_name,
      config: JSON.stringify(template.config, null, 2),
      preview_image_url: template.preview_image_url || '',
      active: template.active,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.modality_id || !formData.name || !formData.code || !formData.component_name) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    saveMutation.mutate({ ...formData, id: editingTemplate?.id });
  };

  const templatesByModality = (templates ?? []).reduce((acc, t) => {
    const key = (t as any).modalityName || (t as any).modality_name || t.game_modalities?.name || 'Sem Modalidade';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {} as Record<string, GameTemplate[]>);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Layers}
        title="Templates de Jogos"
        description="Gerencie as engines/mecanicas base para diferentes tipos de jogos"
        actions={
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Template
          </Button>
        }
      />

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="h-24 bg-muted" />
              <CardContent className="h-20" />
            </Card>
          ))}
        </div>
      ) : Object.keys(templatesByModality).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Layers className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Nenhum template encontrado</h3>
            <p className="text-muted-foreground mb-4">Crie templates para definir as mecanicas dos seus jogos</p>
            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeiro Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        Object.entries(templatesByModality).map(([modalityName, modalityTemplates]) => (
          <div key={modalityName} className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Layers className="w-5 h-5" />
              {modalityName}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {modalityTemplates.map(template => (
                <Card key={template.id} className={!template.active ? 'opacity-60' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <Code className="w-3 h-3" />
                          <code className="text-xs bg-muted px-1 rounded">{template.code}</code>
                        </CardDescription>
                      </div>
                      <Badge variant={template.active ? 'default' : 'secondary'}>
                        {template.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {template.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{template.description}</p>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Settings2 className="w-4 h-4 text-muted-foreground" />
                      <span className="font-mono text-xs">{template.component_name}</span>
                    </div>
                    {template.config && Object.keys(template.config).length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        Config: {Object.keys(template.config).join(', ')}
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEdit(template)}>
                        <Pencil className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => { if (confirm('Remover este template?')) deleteMutation.mutate(template.id); }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Editar Template' : 'Novo Template'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Modalidade *</Label>
              <Select value={formData.modality_id} onValueChange={v => setFormData(p => ({ ...p, modality_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a modalidade" />
                </SelectTrigger>
                <SelectContent>
                  {(modalities ?? []).map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.icon && <span className="mr-2">{m.icon}</span>}
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tmpl-name">Nome do Template *</Label>
              <Input
                id="tmpl-name"
                value={formData.name}
                onChange={e => {
                  const name = e.target.value;
                  setFormData(p => ({ ...p, name, code: p.code || generateCodeFromName(name) }));
                }}
                placeholder="Ex: Slot 3x3 Classico"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tmpl-code">Código Único *</Label>
              <Input
                id="tmpl-code"
                value={formData.code}
                onChange={e => setFormData(p => ({ ...p, code: e.target.value }))}
                placeholder="Ex: slot_3x3_classic"
                className="font-mono text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label>Componente React *</Label>
              <Select value={formData.component_name} onValueChange={v => setFormData(p => ({ ...p, component_name: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPONENT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tmpl-desc">Descrição</Label>
              <Textarea id="tmpl-desc" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Descrição do template..." rows={2} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tmpl-config">Configuração (JSON)</Label>
              <Textarea id="tmpl-config" value={formData.config} onChange={e => setFormData(p => ({ ...p, config: e.target.value }))} placeholder='{"reels": 3, "rows": 3}' className="font-mono text-sm" rows={4} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tmpl-preview">URL da Imagem de Preview</Label>
              <Input id="tmpl-preview" value={formData.preview_image_url} onChange={e => setFormData(p => ({ ...p, preview_image_url: e.target.value }))} placeholder="https://..." />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={formData.active} onCheckedChange={v => setFormData(p => ({ ...p, active: v }))} id="tmpl-active" />
              <Label htmlFor="tmpl-active">Template ativo</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
